import { useAuth } from "../../auth/AuthContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** The translator as react-i18next hands it over, so a helper can take it without widening its type. */
type Translate = ReturnType<typeof useTranslation>["t"];
import { formatDate, formatDateTime } from "../../lib/format";
import { complaintsApi, type Complaint, type ComplaintStaffOption, type EightDStep } from "../../api/complaints";
import type { ApiError } from "../../api/client";
import { eightDProgress } from "../../pages/complaintsFilter";
import { useToast } from "../Toast";
import { Select } from "../Select";
import Ic from "../icons.jsx";

/**
 * The eight steps of an 8D, led through one at a time.
 *
 * <p>The workshop of 11.09.2026 put the 8D on the more serious complaints and left the decision to the
 * quality service. The steps are kept rather than only an attached report, because a buyer who asks for
 * an 8D asks for it step by step: a report can be assembled out of steps, and steps cannot be recovered
 * out of a report.
 *
 * <p>All eight rows exist from the moment the 8D is opened, so the panel shows the whole shape of the
 * work and which part of it is missing. A step with nothing written in it cannot be signed off.
 */
export function ComplaintEightD({
  complaint,
  canOpen,
  onOpened,
}: {
  complaint: Complaint;
  canOpen: boolean;
  onOpened?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canWork = hasPermission("complaints:team_work");
  const [steps, setSteps] = useState<EightDStep[] | null>(null);
  const [staff, setStaff] = useState<ComplaintStaffOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [openingForm, setOpeningForm] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ content: string; ownerUserId: string; dueOn: string }>({
    content: "",
    ownerUserId: "",
    dueOn: "",
  });

  const isOpen = complaint.eightDOpenedAt !== null;

  async function load() {
    if (!isOpen) {
      setSteps([]);
      return;
    }
    try {
      setSteps(await complaintsApi.listEightD(complaint.id));
    } catch {
      setSteps([]);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the loader is stable per complaint and state
  }, [complaint.id, isOpen]);

  useEffect(() => {
    if (!canWork && !canOpen) return;
    complaintsApi.staff().then(setStaff).catch(() => setStaff([]));
  }, [canOpen, canWork]);

  const progress = eightDProgress(steps ?? []);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    try {
      await work();
      await load();
    } catch (e) {
      toast.error(errorText(t, e as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function open8d() {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      setSteps(await complaintsApi.openEightD(complaint.id, reason.trim()));
      setReason("");
      setOpeningForm(false);
      onOpened?.();
    } catch (e) {
      toast.error(errorText(t, e as ApiError));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(s: EightDStep) {
    setEditing(s.step);
    setDraft({ content: s.content ?? "", ownerUserId: s.ownerUserId ?? "", dueOn: s.dueOn ?? "" });
  }

  async function saveStep(step: number) {
    await run(async () => {
      await complaintsApi.updateEightDStep(complaint.id, step, {
        content: draft.content.trim() || null,
        ownerUserId: draft.ownerUserId || null,
        dueOn: draft.dueOn || null,
      });
      setEditing(null);
    });
  }

  if (!isOpen) {
    return (
      <section className="card">
        <div className="card-h">
          <h2>{t("complaints.eightD.title", "8D")}</h2>
        </div>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <p className="sub" style={{ margin: 0 }}>
            {t(
              "complaints.eightD.closedHint",
              "8D se pokreće za ozbiljnije reklamacije i o tome odlučuje kontrola kvaliteta. Osam koraka se otvara zajedno, pa se popunjava.",
            )}
          </p>
          {!canOpen ? (
            <div className="empty">{t("complaints.eightD.notOpened", "8D nije pokrenut.")}</div>
          ) : openingForm ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <textarea
                className="input"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                placeholder={t("complaints.eightD.reasonPlaceholder", "Zašto je ova reklamacija ozbiljna")}
              />
              <div className="toolbar">
                <button type="button" className="btn sm" onClick={() => setOpeningForm(false)}>
                  {t("action.cancel", "Odustani")}
                </button>
                <button type="button" className="btn primary sm" disabled={busy || !reason.trim()} onClick={() => void open8d()}>
                  {t("complaints.eightD.open", "Pokreni 8D")}
                </button>
              </div>
            </div>
          ) : (
            <div className="toolbar">
              <button type="button" className="btn sm" onClick={() => setOpeningForm(true)}>
                <Ic.plus /> {t("complaints.eightD.open", "Pokreni 8D")}
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>{t("complaints.eightD.title", "8D")}</h2>
        <span className={progress.done === progress.total ? "tag ok" : "tag warn"}>
          {t("complaints.eightD.progress", "{{done}} od {{total}}", { done: progress.done, total: progress.total })}
        </span>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="muted">
          {t("complaints.eightD.openedAt", "Pokrenut")}: {formatDateTime(complaint.eightDOpenedAt ?? "")}
          {complaint.eightDOpenedByName ? ` · ${complaint.eightDOpenedByName}` : ""}
        </div>
        {complaint.eightDReason && <div className="sub" style={{ margin: 0 }}>{complaint.eightDReason}</div>}

        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {(steps ?? []).map((s) => (
            <li
              key={s.id}
              style={{
                border: "1px solid var(--border)",
                borderLeft: `3px solid ${s.completedAt ? "var(--ok)" : "var(--border)"}`,
                borderRadius: "var(--r-md)",
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <strong style={{ color: "var(--ink)" }}>
                  D{s.step} · {t(`complaints.eightD.step.D${s.step}`, { defaultValue: `D${s.step}` })}
                </strong>
                {s.completedAt ? (
                  <span className="tag ok">{t("complaints.eightD.stepDone", "završen")}</span>
                ) : (
                  <span className="tag neutral">{t("complaints.eightD.stepOpen", "otvoren")}</span>
                )}
              </div>

              {canWork && editing === s.step ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <textarea
                    className="input"
                    rows={3}
                    value={draft.content}
                    onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                    style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                    placeholder={t("complaints.eightD.contentPlaceholder", "Šta je urađeno u ovom koraku")}
                  />
                  <div className="toolbar">
                    <Select<string>
                      value={draft.ownerUserId}
                      onChange={(v) => setDraft((d) => ({ ...d, ownerUserId: v }))}
                      ariaLabel={t("complaints.eightD.owner", "Nosilac")}
                      style={{ minWidth: 200 }}
                      options={[
                        { value: "", label: t("complaints.eightD.noOwner", "Bez nosioca") },
                        ...staff.map((o) => ({ value: o.id, label: o.name })),
                      ]}
                    />
                    <input
                      className="input"
                      type="date"
                      value={draft.dueOn}
                      onChange={(e) => setDraft((d) => ({ ...d, dueOn: e.target.value }))}
                      aria-label={t("complaints.eightD.due", "Rok")}
                    />
                    <button type="button" className="btn sm" onClick={() => setEditing(null)}>
                      {t("action.cancel", "Odustani")}
                    </button>
                    <button type="button" className="btn primary sm" disabled={busy} onClick={() => void saveStep(s.step)}>
                      {t("complaints.eightD.save", "Upiši")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ color: "var(--ink-2)", fontSize: 13.5, whiteSpace: "pre-wrap" }}>
                    {s.content ?? <span className="muted">{t("complaints.eightD.stepEmpty", "još nije popunjen")}</span>}
                  </div>
                  <div className="muted">
                    {s.ownerName && `${t("complaints.eightD.owner", "Nosilac")}: ${s.ownerName} · `}
                    {s.dueOn && `${t("complaints.eightD.due", "Rok")}: ${formatDate(s.dueOn)}`}
                    {s.completedAt &&
                      ` · ${t("complaints.eightD.doneAt", "Završen")}: ${formatDateTime(s.completedAt)}${
                        s.completedByName ? ` · ${s.completedByName}` : ""
                      }`}
                  </div>
                  {(canWork || canOpen) && (
                    <div className="toolbar">
                      {canWork && !s.completedAt && (
                        <>
                          <button type="button" className="btn sm" disabled={busy} onClick={() => startEdit(s)}>
                            {t("complaints.eightD.edit", "Popuni")}
                          </button>
                          <button
                            type="button"
                            className="btn primary sm"
                            disabled={busy}
                            onClick={() => void run(() => complaintsApi.completeEightDStep(complaint.id, s.step))}
                          >
                            <Ic.check /> {t("complaints.eightD.complete", "Završi korak")}
                          </button>
                        </>
                      )}
                      {canOpen && s.completedAt && (
                        <button
                          type="button"
                          className="btn sm ghost"
                          disabled={busy}
                          onClick={() => void run(() => complaintsApi.reopenEightDStep(complaint.id, s.step))}
                        >
                          {t("complaints.eightD.reopenStep", "Vrati u rad")}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function errorText(t: Translate, err: ApiError): string {
  switch (err.code) {
    case "EIGHT_D_STEP_EMPTY":
      return t("complaints.error.eightDStepEmpty", "Upiši šta je urađeno pre nego što zatvoriš korak.");
    case "EIGHT_D_ALREADY_OPEN":
      return t("complaints.error.eightDAlreadyOpen", "8D je već pokrenut.");
    case "EIGHT_D_NOT_OPEN":
      return t("complaints.error.eightDNotOpen", "Na ovoj reklamaciji 8D nije pokrenut.");
    case "EIGHT_D_STEP_COMPLETED":
      return t("complaints.error.eightDStepCompleted", "Završen korak se ne menja; vrati ga u rad.");
    default:
      return err.message;
  }
}
